import { Box, Typography } from "@mui/material";
import { SearchBar, ServiceCard } from "../components";
import { useTheme } from "../hooks";
import { useEffect, useMemo, useState } from "react";
import { CorporateService } from "../models/Service";
import { useQueryGroupTheme, useQueryService } from "../api/queries";
import { getCorporateType, getStorageToken } from "../utils";
import ServiceSkeleton from "../components/serviceSkeleton";
import { useTranslation } from "react-i18next";

// Module-level so the empty case has ONE identity. A `= []` default in the
// destructure below builds a new array on every render, which changes the
// memo's identity, which re-runs the effect, which sets state, which renders
// again -- the catalogue spin-locked before it had any services to show.
const NO_SERVICES: CorporateService[] = [];

const ServiceList = () => {
  const { theme } = useTheme();
  const token = getStorageToken();
  const [filteredServices, setFilteredServices] = useState<CorporateService[]>(
    []
  );
  const [searchText, setSearchText] = useState("");
  const { t } = useTranslation();
  const { data, isLoading: isLoadingCorporateThemeDetails } =
    useQueryGroupTheme({
      id: theme?.id,
      groupType: getCorporateType(theme?.url_type),
      enable: !!theme?.id,
      authorisation: token || "",
    });

  const corporateId = data?.id || "";

  const { data: serviceList, isLoading: isLoadingServices } = useQueryService({
    corporateId,
    enabled: !!corporateId,
    authorisation: token || "",
  });

  const services = useMemo(() => {
    return (serviceList as CorporateService[]) || NO_SERVICES;
  }, [serviceList]);

  const handleSearch = (event: React.FormEvent<HTMLInputElement>) => {
    const searchText = event.currentTarget.value.toLowerCase();
    if (!searchText) {
      setFilteredServices(services);
      return;
    }
    setSearchText(searchText);

    const filtered = services?.filter(service =>
      service.service_name.toLowerCase().includes(searchText)
    );

    setFilteredServices(filtered);
  };

  useEffect(() => {
    setFilteredServices(services);
  }, [services]);
  const loading = isLoadingCorporateThemeDetails || isLoadingServices;

  return (
    <Box sx={{ display: "grid", gap: 2.5 }}>
      <SearchBar
        placeHolder={t("homepage.search_placeholder")}
        display={"flex"}
        onChange={handleSearch}
      />

      {/* How many services matched, as the dummy counts them. */}
      {!loading && filteredServices.length > 0 && (
        <Typography sx={{ fontSize: 13.5, color: "text.secondary" }}>
          {t("homepage.service_count", { count: filteredServices.length })}
        </Typography>
      )}

      <Box
        sx={{
          display: "grid",
          gap: 1.5,
          // The tiles fill whatever column width the screen gives them,
          // instead of a fixed card width that left the row ragged.
          gridTemplateColumns: {
            xs: "1fr",
            sm: "repeat(auto-fill, minmax(280px, 1fr))",
          },
        }}
        data-testid="service-grid"
      >
        {loading ? (
          <>
            <ServiceSkeleton />
            <ServiceSkeleton />
            <ServiceSkeleton />
          </>
        ) : filteredServices.length === 0 && services.length > 0 ? (
          <Box
            sx={{
              gridColumn: "1 / -1",
              p: 3.5,
              textAlign: "center",
              color: "text.secondary",
              border: "1px dashed",
              borderColor: "divider",
              borderRadius: 3.5,
              bgcolor: "background.paper",
            }}
          >
            {t("homepage.empty")}
          </Box>
        ) : (
          <>
            {filteredServices.map(service => (
              <ServiceCard service={service} key={service.id} />
            ))}
          </>
        )}
      </Box>
    </Box>
  );
};

export default ServiceList;
